/**
 * Estado de servidor das tarefas: consultas, escrita otimista e desfazer.
 *
 * Duas regras guiam este arquivo:
 *
 *  1. **Escrita otimista com rollback** (spec 10.4): a UI muda antes da
 *     resposta. Concluir uma tarefa nao pode ter latencia perceptivel — e a
 *     acao mais repetida do app.
 *  2. **Desfazer, nao confirmar** (spec 10.3): nenhuma acao pergunta "tem
 *     certeza?". Todas executam e oferecem a volta por 8 s.
 *
 * O desfazer e a mutacao INVERSA, nao um endpoint de undo: concluir volta com
 * `status: 'open'`, adiar volta com o prazo anterior, excluir volta com
 * `restore`. Assim o servidor nao guarda estado de sessao por 8 segundos.
 */

import { useCallback } from 'react';
import {
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  deriveQuadrant,
  resolveSnoozePreset,
  uuidv7,
  type CreateTaskPayload,
  type SnoozePreset,
  type Task,
  type UpdateTaskPayload,
} from '@cleartask/shared';
import { api, type TaskListQuery, type TaskListResponse } from '../../lib/api.js';
import { isQueuedOffline, withOfflineQueue } from '../../lib/sync.js';
import { useToast } from '../../components/ui/Toast.js';
import { useCurrentUser } from '../../session.js';

export const taskKeys = {
  all: ['tasks'] as const,
  // A janela entra na chave: sem ela, avancar uma semana no calendario
  // reaproveitaria o cache da semana anterior e a grade mostraria dados da
  // data errada ate a revalidacao chegar.
  list: (query: TaskListQuery) =>
    [
      'tasks',
      query.view,
      query.listId ?? null,
      query.tagId ?? null,
      // Sem `parentId` na chave, a lista de subtarefas de uma tarefa serviria
      // a outra: mesma view, mesmos demais filtros, cache compartilhado.
      query.parentId ?? null,
      query.includeDone ?? false,
      query.from ?? null,
      query.to ?? null,
      // Sem o termo na chave, duas buscas diferentes compartilhariam o mesmo
      // cache e a segunda mostraria o resultado da primeira ate revalidar.
      query.q ?? null,
      /*
        Teto e recorte entram no FIM da tupla de proposito: `INDICE_INCLUDE_DONE`
        depende da posicao de `includeDone`, e acrescentar campo no meio quebra
        o patch otimista em silencio — foi o que aconteceu quando `parentId`
        entrou (§8r). No fim, nada se desloca.

        Os dois precisam estar na chave: "as 300 primeiras" e "as 600
        primeiras" sao respostas diferentes, e sem isso pedir mais devolveria o
        cache antigo e o botao pareceria nao fazer nada.
      */
      query.limit ?? null,
      query.doneDays ?? null,
    ] as const,
  // O detalhe por id fica sob o MESMO prefixo `['tasks']` das listas, de
  // proposito: toda invalidacao de tarefa ja o alcanca, sem uma segunda regra
  // para lembrar. O preco esta em `ehChaveDeLista`.
  byId: (id: string) => ['tasks', 'byId', id] as const,
};

/**
 * Distingue o cache de LISTA do cache de detalhe por id.
 *
 * Os dois vivem sob `['tasks']` e guardam formatos diferentes: a lista e
 * `{ tasks: [] }`, o detalhe e `{ task }`. Sem esta separacao, `patchCaches`
 * chamaria o transform com `undefined` no lugar do array e QUALQUER escrita
 * otimista quebraria — mas so depois de alguem abrir um link direto para uma
 * tarefa que nao esta em nenhuma lista em cache, que e o unico caminho que
 * popula o cache por id. Ou seja: um defeito que nao aparece no uso comum e
 * derruba a tela de quem chegou por link.
 */
function ehChaveDeLista(key: QueryKey): boolean {
  return key[1] !== 'byId';
}

/**
 * Posicao de `includeDone` na tupla montada por `taskKeys.list`.
 *
 * Era 4 ate `parentId` entrar na chave (§8r) e empurrar tudo uma casa. O
 * comentario de `chaveIncluiConcluidas` avisava exatamente sobre isto: a
 * reordenacao passa no typecheck e quebra o patch otimista em silencio — a
 * tarefa concluida some da visao "Todas" em vez de ficar riscada. Quem
 * acrescentar um campo aqui precisa mexer nesta linha tambem.
 */
const INDICE_INCLUDE_DONE = 5;

/**
 * Le `includeDone` de volta de uma chave de cache.
 *
 * Mora colado a `taskKeys.list` de proposito: e o unico lugar que depende da
 * POSICAO dos campos na tupla, e separar os dois faria uma reordenacao da chave
 * passar no typecheck enquanto quebra o patch otimista em silencio.
 *
 * Existe porque concluir significa coisas diferentes em cada cache: onde a
 * consulta pediu concluidas, a tarefa muda de estado e FICA; onde nao pediu,
 * ela sai da lista. Um patch unico para todos os caches erraria num dos dois.
 */
export function chaveIncluiConcluidas(key: QueryKey): boolean {
  return key[INDICE_INCLUDE_DONE] === true;
}

/**
 * Uma tarefa pelo id, para a rota `/tarefas/:id`.
 *
 * **So vai a rede quando a tarefa NAO esta em nenhuma lista em cache.** Abrir
 * o detalhe a partir de um card e o caso comum, e nele o dado ja esta em mãos;
 * pedir de novo gastaria um round-trip para receber o que a tela ja mostra. A
 * consulta existe para o outro caso, que e o que justifica ter endereco: link
 * colado noutro aparelho, ou F5 em cima do detalhe aberto.
 */
export function useTask(id: string | undefined) {
  const queryClient = useQueryClient();

  const emCache =
    id === undefined
      ? undefined
      : queryClient
          .getQueriesData<TaskListResponse>({ queryKey: taskKeys.all })
          .filter(([key]) => ehChaveDeLista(key))
          .flatMap(([, data]) => data?.tasks ?? [])
          .find((task) => task.id === id);

  const query = useQuery({
    queryKey: taskKeys.byId(id ?? ''),
    queryFn: () => api.tasks.byId(id as string),
    enabled: id !== undefined && emCache === undefined,
    staleTime: 30_000,
  });

  return {
    // O cache das listas vence a resposta da rede porque e ele que as escritas
    // otimistas patcham: preferir `query.data` faria uma edicao feita com o
    // detalhe aberto por link demorar a aparecer no proprio painel.
    task: emCache ?? query.data?.task ?? null,
    // "Ainda estou procurando" e diferente de "nao achei", e sem os dois a tela
    // pisca um erro antes de a consulta voltar. Um unico sinal basta para
    // distinguir: sem tarefa e sem consulta em voo, nao ha o que esperar.
    procurando: id !== undefined && emCache === undefined && query.isPending,
  };
}

export function useTaskList(query: TaskListQuery) {
  return useQuery({
    queryKey: taskKeys.list(query),
    queryFn: () => api.tasks.list(query),
    // As tarefas mudam pouco entre telas; 30 s evita um round-trip a cada
    // troca de aba sem deixar dado visivelmente velho na tela.
    staleTime: 30_000,
  });
}

type Snapshot = Array<[QueryKey, TaskListResponse | undefined]>;

/**
 * Aplica uma transformacao a TODAS as listas de tarefas em cache e devolve o
 * estado anterior para rollback.
 *
 * Vale para todas porque a mesma tarefa aparece em varias views (Hoje, matriz,
 * lista). Atualizar so a view visivel deixaria as outras mentindo ate a
 * proxima revalidacao.
 */
async function patchCaches(
  qc: QueryClient,
  transform: (tasks: Task[], key: QueryKey) => Task[],
): Promise<Snapshot> {
  // Cancela requisicoes em voo: uma resposta antiga chegando depois do patch
  // otimista sobrescreveria a mudanca que o usuario acabou de ver.
  await qc.cancelQueries({ queryKey: taskKeys.all });

  // So os caches de LISTA: o detalhe por id vive sob o mesmo prefixo e guarda
  // `{ task }`, sem o array que o transform espera (ver `ehChaveDeLista`).
  const previous = qc
    .getQueriesData<TaskListResponse>({ queryKey: taskKeys.all })
    .filter(([key]) => ehChaveDeLista(key));

  // Um `setQueriesData` so seria mais curto, mas nao entrega a chave ao
  // transform — e sem ela nao da para saber se aquele cache pediu as
  // concluidas. Iterar sobre o snapshot resolve e nao custa nada: sao poucas
  // consultas de tarefa vivas ao mesmo tempo.
  for (const [key, value] of previous) {
    if (!value) continue;
    qc.setQueryData<TaskListResponse>(key, { ...value, tasks: transform(value.tasks, key) });
  }

  return previous;
}

function rollback(qc: QueryClient, snapshot: Snapshot | undefined): void {
  if (!snapshot) return;
  for (const [key, value] of snapshot) qc.setQueryData(key, value);
}

function replaceTask(id: string, patch: Partial<Task>) {
  return (tasks: Task[]): Task[] =>
    tasks.map((task) => (task.id === id ? { ...task, ...patch } : task));
}

function dropTask(id: string) {
  return (tasks: Task[]): Task[] => tasks.filter((task) => task.id !== id);
}

/**
 * Devolve a tarefa aos caches de onde a exclusao a tirou.
 *
 * So aos que a CONTINHAM no instante da exclusao (`snapshot`), e nao a todos:
 * decidir se uma tarefa pertence a um recorte (Hoje, matriz, lista X) e a
 * conta que o servidor faz, e repeti-la aqui erraria em algum caso. Quem tinha
 * a tarefa antes de apagar volta a te-la depois de desfazer — sem rede, isso e
 * tudo o que se pode afirmar, e e exatamente o que a pessoa espera ver.
 */
function reinsertTask(task: Task, snapshot: Snapshot | undefined) {
  const continham = new Set(
    (snapshot ?? [])
      .filter(([, data]) => data?.tasks.some((t) => t.id === task.id))
      .map(([key]) => hashKey(key)),
  );

  return (tasks: Task[], key: QueryKey): Task[] =>
    continham.has(hashKey(key)) && !tasks.some((t) => t.id === task.id)
      ? sortLikeServer([...tasks, { ...task, deletedAt: null }])
      : tasks;
}

/**
 * O patch otimista de concluir, que depende do cache.
 *
 * Onde a consulta pediu `includeDone` (a visao "Todas", o calendario, a
 * paleta), a tarefa vira `done` e permanece: e ali que o risco no titulo tem de
 * aparecer na hora. Onde nao pediu (Hoje, 7 dias, matriz), o servidor filtra
 * `status = 'open'` e a tarefa sai da lista.
 *
 * `completedAt` entra junto porque a visao "Todas" ordena os executados por
 * ele: sem o carimbo otimista, a tarefa recem-concluida cairia no fim do grupo
 * e so subiria para o topo na revalidacao.
 *
 * `at` nem sempre e o agora: numa tarefa que ja tinha carimbo (concluida,
 * reaberta e concluida de novo) o servidor devolve o carimbo ORIGINAL, e
 * prever o agora aqui faria a tarefa saltar de posicao quando a resposta
 * chegasse. Quem chama resolve qual dos dois vale.
 */
function completeTask(id: string, at: number) {
  return (tasks: Task[], key: QueryKey): Task[] =>
    chaveIncluiConcluidas(key)
      ? replaceTask(id, { status: 'done', completedAt: at })(tasks)
      : dropTask(id)(tasks);
}

/** Mesma ordem do `ORDER BY` da API: prazo, sem prazo por ultimo, depois posicao. */
function sortLikeServer(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.dueAt === null && b.dueAt !== null) return 1;
    if (a.dueAt !== null && b.dueAt === null) return -1;
    if (a.dueAt !== null && b.dueAt !== null && a.dueAt !== b.dueAt) return a.dueAt - b.dueAt;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.createdAt - b.createdAt;
  });
}

function useInvalidateTasks(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: taskKeys.all });
  }, [qc]);
}

/**
 * Gera o id de uma tarefa nova.
 *
 * UUID v7 no CLIENTE e o que torna `POST /tasks` idempotente e vai permitir a
 * criacao offline da fase 7 — o id nao depende de resposta do servidor.
 */
export function newTaskId(): string {
  return uuidv7();
}

// --------------------------------------------------------------------- criar

/**
 * Monta o registro completo para a inclusao otimista.
 *
 * O quadrante e derivado com a MESMA funcao do servidor (`@cleartask/shared`).
 * Uma copia da formula no cliente faria o card pular de quadrante quando a
 * resposta chegasse.
 */
function optimisticTask(payload: CreateTaskPayload, ownerId: string, now: number): Task {
  const priority = payload.priority ?? 0;
  const dueAt = payload.dueAt ?? null;

  // Um quadrante FIXADO pelo cliente manda sobre a derivacao — e a regra que o
  // servidor aplica no INSERT. Sem esta linha, a tarefa criada pelo "+" de um
  // quadrante da matriz nasceria no quadrante derivado (Q4, para o que nao tem
  // prazo nem prioridade) e saltaria para o quadrante certo quando a resposta
  // chegasse. O card pulando de lugar e exatamente o sintoma que manter
  // `deriveQuadrant` em `shared` existe para evitar.
  const quadrantAuto = payload.quadrantAuto ?? true;
  const quadrant =
    !quadrantAuto && payload.quadrant != null
      ? payload.quadrant
      : deriveQuadrant({ priority, dueAt }, now);

  return {
    id: payload.id,
    ownerId,
    listId: payload.listId ?? null,
    parentId: payload.parentId ?? null,
    title: payload.title,
    notes: payload.notes ?? null,
    status: 'open',
    priority,
    quadrant,
    quadrantAuto,
    dueAt,
    allDay: payload.allDay ?? false,
    reminderAt: payload.reminderAt ?? null,
    rrule: payload.rrule ?? null,
    seriesId: null,
    estimatePomos: payload.estimatePomos ?? 0,
    sortOrder: payload.sortOrder ?? 0,
    completedAt: null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    tagIds: payload.tagIds ?? [],
  };
}

/**
 * Criar funciona sem rede (spec 15, fase 7).
 *
 * O `optimisticTask` serve duas vezes: no cache, para a tarefa aparecer na
 * hora, e como resposta do caminho offline — assim a mutacao "tem sucesso" e o
 * card fica na tela em vez de sofrer rollback. A gravacao de verdade acontece
 * quando a fila drena.
 */
export function useCreateTask() {
  const qc = useQueryClient();
  const user = useCurrentUser();
  const invalidate = useInvalidateTasks();

  return useMutation({
    mutationFn: (payload: CreateTaskPayload) =>
      withOfflineQueue({ entity: 'task', action: 'create', entityId: payload.id, payload }, () =>
        api.tasks.create(payload),
      ),

    onMutate: async (payload) => {
      const task = optimisticTask(payload, user.id, Date.now());
      // Reordenar pela mesma regra do servidor evita o card saltar de posicao
      // quando a lista recarregar.
      return patchCaches(qc, (tasks) => sortLikeServer([...tasks, task]));
    },

    onError: (_error, _payload, snapshot) => rollback(qc, snapshot),
    onSettled: invalidate,
  });
}

// ------------------------------------------------------------------- alterar

export function useUpdateTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateTaskPayload }) =>
      withOfflineQueue({ entity: 'task', action: 'update', entityId: id, payload: patch }, () =>
        api.tasks.update(id, patch),
      ),

    onMutate: ({ id, patch }) => patchCaches(qc, replaceTask(id, patch as Partial<Task>)),

    onError: (_error, _vars, snapshot) => rollback(qc, snapshot),
    onSettled: invalidate,
  });
}

// ------------------------------------------------------------------ concluir

export function useCompleteTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (task: Task) =>
      withOfflineQueue({ entity: 'task', action: 'complete', entityId: task.id, payload: {} }, () =>
        api.tasks.complete(task.id),
      ),

    // O carimbo que ja existe vence o agora: o servidor preserva a hora da
    // primeira conclusao (`db/tasks.ts`), entao prever `Date.now()` numa
    // tarefa reaberta mostraria uma data que a resposta vai desmentir.
    onMutate: (task) => patchCaches(qc, completeTask(task.id, task.completedAt ?? Date.now())),

    onError: (_error, _task, snapshot) => rollback(qc, snapshot),

    onSuccess: (result, task) => {
      // Offline nao da para saber se a serie avancou: quem gera a proxima
      // ocorrencia e o servidor, ao drenar a fila. Anunciar "proxima criada"
      // aqui seria inventar um fato.
      const pendente = isQueuedOffline(result);
      const proxima = pendente ? null : result.next;

      showToast({
        message: pendente
          ? 'Concluída. Será sincronizada.'
          : proxima
            ? 'Concluída. Próxima ocorrência criada.'
            : 'Concluída',
        action: {
          label: 'Desfazer',
          onAction: () => {
            void (async () => {
              // O desfazer tambem passa pela fila: em modo aviao ele precisa
              // funcionar como qualquer outra escrita.
              await withOfflineQueue(
                {
                  entity: 'task',
                  action: 'update',
                  entityId: task.id,
                  payload: { status: 'open' },
                },
                () => api.tasks.update(task.id, { status: 'open' }),
              );

              // Desfazer uma recorrencia precisa remover a instancia que a
              // conclusao gerou; deixa-la para tras duplicaria a tarefa.
              if (proxima) {
                await withOfflineQueue(
                  { entity: 'task', action: 'delete', entityId: proxima.id, payload: {} },
                  () => api.tasks.remove(proxima.id),
                );
              }
              invalidate();
            })();
          },
        },
      });
    },

    onSettled: invalidate,
  });
}

// ------------------------------------------------------------------- reabrir

/**
 * Desmarca uma tarefa concluida.
 *
 * Existe desde que a visao "Todas" passou a mostrar os executados: um checkbox
 * marcado precisa desmarcar. Sem esta mutacao, clicar nele chamaria
 * `POST /complete` numa tarefa ja concluida — e numa tarefa com `rrule` isso
 * GERA OUTRA OCORRENCIA da serie, duplicando a agenda a cada clique.
 *
 * E `PATCH { status: 'open' }`, a mesma escrita do desfazer de concluir, e nao
 * um endpoint proprio: o servidor nao guarda estado de sessao, e reabrir e
 * exatamente a mutacao inversa (spec 10.3).
 *
 * **`completedAt` sobrevive.** Desde 19/08/2026 o servidor nao zera a coluna ao
 * reabrir, e reconcluir devolve o carimbo original (`db/tasks.ts`): desmarcar
 * sem querer e marcar de novo deixou de mover para hoje uma conclusao da
 * semana passada, que `GET /tasks/stats` contaria no dia errado. A tarefa
 * reaberta continua saindo do grupo "executados" porque quem responde "esta
 * concluida?" e o `status`, nao a existencia do carimbo.
 */
export function useReopenTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (task: Task) =>
      withOfflineQueue(
        { entity: 'task', action: 'update', entityId: task.id, payload: { status: 'open' } },
        () => api.tasks.update(task.id, { status: 'open' }),
      ),

    // So o `status` muda: o carimbo fica onde esta, porque e o que o servidor
    // faz. Zera-lo aqui perderia, no cache, a hora que o desfazer precisa — e
    // a tarefa ja sai do grupo "executados" pelo status.
    onMutate: (task) => patchCaches(qc, replaceTask(task.id, { status: 'open' })),

    onError: (_error, _task, snapshot) => rollback(qc, snapshot),

    onSuccess: (_result, task) => {
      showToast({
        message: 'Reaberta',
        action: {
          label: 'Desfazer',
          onAction: () => {
            void (async () => {
              // `PATCH { status: 'done' }`, e nao `POST /complete`: a rota de
              // concluir geraria a proxima ocorrencia de uma serie que ja
              // avancou quando a tarefa foi concluida da primeira vez.
              await withOfflineQueue(
                {
                  entity: 'task',
                  action: 'update',
                  entityId: task.id,
                  payload: { status: 'done' },
                },
                () => api.tasks.update(task.id, { status: 'done' }),
              );
              invalidate();
            })();
          },
        },
      });
    },

    onSettled: invalidate,
  });
}

// --------------------------------------------------------------------- adiar

export function useSnoozeTask() {
  const qc = useQueryClient();
  const user = useCurrentUser();
  const invalidate = useInvalidateTasks();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      task,
      payload,
    }: {
      task: Task;
      payload: { preset: SnoozePreset } | { dueAt: number };
    }) =>
      withOfflineQueue(
        {
          entity: 'task',
          action: 'update',
          entityId: task.id,
          // A fila carrega o `dueAt` ja RESOLVIDO, nunca o preset: "amanha as
          // 09:00" gravado hoje e sincronizado depois de amanha significaria
          // outro dia. O preset e relativo ao momento em que a pessoa clicou.
          payload: {
            dueAt:
              'preset' in payload
                ? resolveSnoozePreset(payload.preset, Date.now(), user.timezone)
                : payload.dueAt,
          },
        },
        () => api.tasks.snooze(task.id, payload),
      ),

    onMutate: ({ task, payload }) => {
      // O preset e resolvido no cliente com a MESMA funcao do servidor, no
      // fuso do usuario — e por isso que ela mora em `shared`. Sem isso, a
      // previsao otimista mostraria uma data e o servidor gravaria outra.
      const dueAt =
        'preset' in payload
          ? resolveSnoozePreset(payload.preset, Date.now(), user.timezone)
          : payload.dueAt;

      return patchCaches(qc, replaceTask(task.id, { dueAt }));
    },

    onError: (_error, _vars, snapshot) => rollback(qc, snapshot),

    onSuccess: (_result, { task }) => {
      const anterior = task.dueAt;
      showToast({
        message: 'Adiada',
        action: {
          label: 'Desfazer',
          onAction: () => {
            void (async () => {
              /*
                O mesmo cuidado do desfazer da exclusao (revisao de
                13/10/2026): patch otimista para o prazo voltar na hora mesmo
                sem rede, e — se o servidor RECUSAR, que a fila nao absorve —
                rollback e aviso. Antes a recusa virava rejeicao sem dono: nada
                na tela dizia que o prazo continuava adiado.
              */
              const snapshot = await patchCaches(qc, replaceTask(task.id, { dueAt: anterior }));
              try {
                await withOfflineQueue(
                  {
                    entity: 'task',
                    action: 'update',
                    entityId: task.id,
                    payload: { dueAt: anterior },
                  },
                  () => api.tasks.update(task.id, { dueAt: anterior }),
                );
              } catch {
                rollback(qc, snapshot);
                showToast({ message: 'Não foi possível desfazer o adiamento.' });
              } finally {
                invalidate();
              }
            })();
          },
        },
      });
    },

    onSettled: invalidate,
  });
}

// ------------------------------------------------------------------- excluir

/**
 * Tira a tarefa da lixeira.
 *
 * Viveu inline dentro de `features/trash/TrashList.tsx` ate 12/08/2026, e por
 * isso a lixeira era o UNICO lugar do app capaz de restaurar: o painel de uma
 * tarefa excluida aberta por link so sabia mandar a pessoa procurar a mesma
 * tarefa noutra tela. Uma mutacao que mora dentro de uma tela vira, na pratica,
 * uma funcionalidade que so aquela tela tem.
 *
 * O patch otimista e PARCIAL: a tarefa sai da lixeira e o detalhe por id
 * passa a mostra-la fora dela, mas ela nao e inserida nas listas visiveis. Ela
 * precisa REAPARECER em consultas que nao a continham (`view=list` exclui a
 * lixeira), e inseri-la a mao em cada cache exigiria saber, por chave, se ela
 * pertence aquele recorte — a mesma conta que o servidor ja faz. A invalidacao
 * resolve isso em um round-trip.
 *
 * Passa pela fila desde 13/10/2026 (`restore` entrou no enum de acoes): sem
 * rede, a restauracao espera a reconexao em vez de falhar — e sem o patch, a
 * lixeira continuaria mostrando a tarefa e o painel aberto por link
 * continuaria dizendo "na lixeira" ate reconectar, como se o clique nao
 * tivesse feito nada.
 */
export function useRestoreTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (task: Task) =>
      withOfflineQueue({ entity: 'task', action: 'restore', entityId: task.id, payload: {} }, () =>
        api.tasks.restore(task.id),
      ),

    onMutate: async (task) => {
      const listas = await patchCaches(qc, (tasks, key) =>
        key[1] === 'trash' ? dropTask(task.id)(tasks) : tasks,
      );

      // O detalhe por id e escrito mesmo que nao existisse: quem abriu a tarefa
      // pela lixeira a leu do cache da LISTA, que acabou de perde-la. Sem esta
      // copia, `useTask` iria a rede atras dela — e offline o painel ficaria
      // preso em "Abrindo a tarefa…".
      const chave = taskKeys.byId(task.id);
      const detalhe = qc.getQueryData<{ task: Task }>(chave);
      qc.setQueryData<{ task: Task }>(chave, { task: { ...task, deletedAt: null } });

      return { listas, detalhe };
    },

    onError: (_error, task, contexto) => {
      rollback(qc, contexto?.listas);
      const chave = taskKeys.byId(task.id);
      // `setQueryData(chave, undefined)` nao faz nada no TanStack Query; o
      // detalhe que nao existia antes precisa ser removido de fato.
      if (contexto?.detalhe) qc.setQueryData(chave, contexto.detalhe);
      else qc.removeQueries({ queryKey: chave, exact: true });
    },

    onSuccess: (result) =>
      showToast({
        message: isQueuedOffline(result) ? 'Restaurada. Será sincronizada.' : 'Restaurada',
      }),
    onSettled: invalidate,
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (task: Task) =>
      withOfflineQueue({ entity: 'task', action: 'delete', entityId: task.id, payload: {} }, () =>
        api.tasks.remove(task.id),
      ),

    onMutate: (task) => patchCaches(qc, dropTask(task.id)),
    onError: (_error, _task, snapshot) => rollback(qc, snapshot),

    onSuccess: (_result, task, snapshot) => {
      showToast({
        message: 'Movida para a lixeira',
        action: {
          label: 'Desfazer',
          onAction: () => {
            void (async () => {
              // Patch otimista primeiro: sem rede, a invalidacao abaixo nao
              // tem a quem perguntar, e a tarefa so voltaria a tela depois de
              // reconectar — o desfazer pareceria nao ter funcionado.
              const antes = await patchCaches(qc, reinsertTask(task, snapshot));

              // Pela fila, como a exclusao: em modo aviao o desfazer precisa
              // funcionar igual a qualquer outra escrita. O servidor recebe o
              // `delete` e o `restore` no mesmo lote, nessa ordem.
              try {
                await withOfflineQueue(
                  { entity: 'task', action: 'restore', entityId: task.id, payload: {} },
                  () => api.tasks.restore(task.id),
                );
              } catch {
                // A fila so absorve falha de REDE. Uma recusa do servidor
                // (404, 4xx, 500) chega aqui, e sem desfazer o patch a tarefa
                // ficaria na tela como fantasma — viva no cache, na lixeira no
                // servidor — ate a proxima revalidacao, sem ninguem avisar.
                rollback(qc, antes);
                showToast({ message: 'Não foi possível restaurar a tarefa.' });
              } finally {
                invalidate();
              }
            })();
          },
        },
      });
    },

    onSettled: invalidate,
  });
}
